/**
 * @fileoverview Stable per-element ids for page scrapes, plus the inline markers that carry an id
 * through the string assembly `scrapeNode` performs.
 *
 * `scrapeNode` builds its output by joining, wrapping and newline-collapsing the content of nested
 * nodes, so a parallel "one id per line" array cannot survive the recursion. Instead each claim
 * travels inside the content itself, wrapped in a sentinel character that never occurs in
 * dashboard text, is swapped for the element's persisted id once the scrape is assembled, and is
 * pulled back out line by line when the read is rendered.
 */

import type { ScrapedLine } from "./types";

/** Attribute holding an element's scrape id, written the first time the element is scraped. */
export const scrapeIdAttribute = "data-operator-scrape-id";

/**
 * Wraps an id inside scraped content. U+E000 is a private-use code point: it has no meaning of
 * its own, so it cannot appear in rendered dashboard text or in a translation.
 */
const markerCharacter = "\uE000";
const firstMarkerPattern = /\uE000([^\uE000]*)\uE000/;
const allMarkersPattern = /\uE000([^\uE000]*)\uE000/g;
const strayMarkerPattern = /\uE000/g;

let nextScrapeIdNumber = 0;

/**
 * Ids this session assigned, keyed by the element they belong to.
 *
 * The attribute on the element is a debug aid. It is not the source of truth: page content can
 * already carry `data-operator-scrape-id`, and a clone copies it, so trusting the attribute would
 * let that content choose a line's identity.
 */
let scrapeIdByElement = new WeakMap<HTMLElement, string>();

/**
 * Returns the element's scrape id, assigning and persisting one on first use.
 *
 * The id is remembered for as long as the element is: a later scrape of the same element reports
 * the same id, which is what lets a later read omit it when the content is the same. An element
 * React tears down and recreates gets a fresh id and is reported as new content. An id already
 * sitting on the element is ignored unless this session assigned it to that element.
 *
 * @param element The element to identify.
 * @return The element's stable scrape id.
 */
export const ensureScrapeId = (element: HTMLElement): string => {
  const remembered = scrapeIdByElement.get(element);
  if (remembered !== undefined) {
    return remembered;
  }

  nextScrapeIdNumber += 1;
  const id = `e${nextScrapeIdNumber.toString(36)}`;
  scrapeIdByElement.set(element, id);
  element.setAttribute(scrapeIdAttribute, id);
  return id;
};

/** Resets the id counter so generated ids are deterministic per test case. Test-only. */
export const resetScrapeIdCounterForTesting = () => {
  nextScrapeIdNumber = 0;
  scrapeIdByElement = new WeakMap();
};

/**
 * @param content Scraped content, marked or not.
 * @return Whether the content carries at least one id marker.
 */
export const hasScrapeMarker = (content: string): boolean => content.includes(markerCharacter);

/**
 * @param content Scraped content.
 * @return The content with every id marker, and any unpaired sentinel character, removed.
 */
export const stripScrapeMarkers = (content: string): string =>
  content.replace(allMarkersPattern, "").replace(strayMarkerPattern, "");

const markLine = (id: string, line: string): string => `${markerCharacter}${id}${markerCharacter}${line}`;

/**
 * Gives every line of `content` that no element has claimed yet to the element `getClaim`
 * identifies. Lines already carrying a marker came from a nested element that owns them and are
 * left alone, so an element whose children own every line claims nothing.
 *
 * The first line an element owns always carries its bare claim. Each further line gets a `#N`
 * suffix, counting from 1, so it stays individually diffable. Growing or shrinking by a line
 * therefore keeps the id of the line that remains, instead of renaming every line — a rename
 * would be reported as the element being removed and added back.
 *
 * @param content Scraped content, possibly already carrying markers from nested elements.
 * @param getClaim Lazily identifies the owning element: its scrape id, or a token
 *   {@link resolveScrapeMarkers} later turns into one.
 * @return The content with every non-empty line marked.
 */
export const markScrapeLines = (content: string, getClaim: () => string): string => {
  if (!content) {
    return content;
  }

  const lines = content.split("\n");
  const unmarkedIndexes = lines.flatMap((line, index) => (line && !hasScrapeMarker(line) ? [index] : []));
  if (unmarkedIndexes.length === 0) {
    return content;
  }

  const claim = getClaim();
  unmarkedIndexes.forEach((index, ordinal) => {
    const id = ordinal === 0 ? claim : `${claim}#${ordinal}`;
    lines[index] = markLine(id, lines[index]);
  });
  return lines.join("\n");
};

/**
 * Turns the claim tokens in a finished scrape into persisted scrape ids.
 *
 * While a scrape is assembled, markers hold the index of the claiming element in `elements`
 * rather than an id, because a nested element's claim is dropped when an ancestor takes over its
 * whole line. Resolving only what survives means an element is tagged in the DOM only when a
 * line is actually reported under it.
 *
 * @param content Scraped content whose markers hold indexes into `elements`.
 * @param elements The elements that made claims, in index order.
 * @return The content with every marker holding the element's scrape id.
 */
export const resolveScrapeMarkers = (content: string, elements: HTMLElement[]): string =>
  content.replace(allMarkersPattern, (_marker, claim: string) => {
    // Only a claim this scrape wrote — an index into `elements`, optionally `#N` — resolves.
    // Anything else was planted in page content and is dropped rather than becoming a line id.
    const parsed = /^(\d+)(?:#(\d+))?$/.exec(claim);
    const element = parsed ? elements[Number(parsed[1])] : undefined;
    if (!parsed || !element) {
      return "";
    }
    const id = parsed[2] === undefined ? ensureScrapeId(element) : `${ensureScrapeId(element)}#${parsed[2]}`;
    return markLine(id, "");
  });

/**
 * @param line One line of marked scrape output.
 * @return The line's id, taken from the first marker on it, and its text.
 */
export const parseMarkedLine = (line: string): ScrapedLine => {
  const match = firstMarkerPattern.exec(line);
  return { id: match ? match[1] : null, text: stripScrapeMarkers(line) };
};
